/**
 * SessionLeaderCell — MED-812 / E4.0a
 *
 * Inline Session Leader designation affordance for the cohort detail Sessions
 * list/card. Renders one of three states:
 *
 *   • Not applicable — returns null (non-peer-led modality OR feature flag off)
 *   • Editable      — Mantine Select with current group roster, current leader
 *                     pre-selected; onChange calls the API and notifies on
 *                     success/error.
 *   • Locked        — Plain read-only text showing the current leader (or
 *                     "Unassigned"). Activated once now() >= scheduled_start.
 *
 * The cell is intentionally self-contained: SessionRow drops one of these in
 * its right-side group and lets the cell decide whether to render at all.
 */
import { useState, type ReactNode } from 'react';
import { Select, Text, Group, Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useFeatureFlag } from '../../../../stores/featureFlags';
import { sessionsApi } from '../../../../services/api/sessions';
import type { GroupMember } from '../../../../services/api/groups';
import type { SessionWithDetailsAndRsvp } from '../../../../services/api/sessions';
import {
  formatFormalNameWithPreferred,
  getPreferredParenthetical,
} from '../../../../utils/participantDisplayName';

interface SessionLeaderCellProps {
  session: SessionWithDetailsAndRsvp;
  cohortId: string;
  groupMembers: GroupMember[];
  /** Called after a successful assignment so the parent can refetch sessions. */
  onUpdated?: () => void;
  /**
   * MED-1181 / AC2: the cohort's group-coaching per-modality Live Session
   * Delivery toggle. The inline Leader cell is an LSD affordance, so it must be
   * gated on this in addition to the global flag and the peer-led modality.
   * Defaults false (fail closed) so a caller that hasn't opted in renders nothing.
   */
  liveSessionDeliveryModalityEnabled?: boolean;
}

const UNASSIGNED_VALUE = '__unassigned__';

function formatMemberLabel(m: GroupMember): string {
  // MED-1162 (AC5) — full formal name + "(preferred)" when it differs. Plain
  // string because a native <Select> option label cannot render JSX.
  return formatFormalNameWithPreferred(m.first_name, m.last_name, m.preferred_name, m.email);
}

export function SessionLeaderCell({
  session,
  cohortId,
  groupMembers,
  onUpdated,
  liveSessionDeliveryModalityEnabled = false,
}: SessionLeaderCellProps) {
  const isLiveSessionDeliveryFlagEnabled = useFeatureFlag('live_session_delivery');
  const [isSaving, setIsSaving] = useState(false);

  // Gate 1: global feature flag
  if (!isLiveSessionDeliveryFlagEnabled) return null;

  // Gate 2 (MED-1181 / AC2): the cohort's per-modality Live Session Delivery
  // toggle. Without this the cell rendered for any peer-led cohort once the
  // global flag was on, including classic MED01 cohorts.
  if (!liveSessionDeliveryModalityEnabled) return null;

  // Gate 3: modality must be group_coaching + peer_led
  if (!session.is_peer_led) return null;

  // Determine lock state from scheduled_start.
  const scheduledStartTime = session.scheduled_start
    ? new Date(session.scheduled_start).getTime()
    : null;
  const isLocked =
    scheduledStartTime !== null &&
    !Number.isNaN(scheduledStartTime) &&
    Date.now() >= scheduledStartTime;

  // Build the display label for the current leader (locked state).
  // MED-1162 (AC5) — full formal name with a dimmed "(preferred)" parenthetical
  // when the leader's preferred name differs from their first name.
  const currentLeaderLabel: ReactNode = (() => {
    if (!session.session_leader_id) return 'Unassigned';
    const first = session.session_leader_first_name;
    const last = session.session_leader_last_name;
    if (first || last) {
      const formal = `${first ?? ''} ${last ?? ''}`.trim();
      const preferred = getPreferredParenthetical(session.session_leader_preferred_name, first);
      return (
        <>
          {formal}
          {preferred && (
            <Text span c="dimmed" fw={400}>
              {' '}
              ({preferred})
            </Text>
          )}
        </>
      );
    }
    // Fall back to the roster (covers the case where the leader was just
    // assigned this session and the parent hasn't refreshed denormalized
    // fields yet).
    const member = groupMembers.find((m) => m.user_id === session.session_leader_id);
    return member ? formatMemberLabel(member) : 'Unknown';
  })();

  if (isLocked) {
    return (
      <Group gap={4} wrap="nowrap" style={{ textAlign: 'right' }}>
        <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
          Leader
        </Text>
        <Text size="sm" fw={500}>
          {currentLeaderLabel}
        </Text>
      </Group>
    );
  }

  const handleChange = async (value: string | null) => {
    // Mantine Select can return null when cleared, but we set `clearable={false}`
    // so this is just a defensive check.
    if (!value || value === UNASSIGNED_VALUE) return;
    if (value === session.session_leader_id) return; // no-op

    setIsSaving(true);
    try {
      await sessionsApi.assignSessionLeader(cohortId, session.id, value);
      notifications.show({
        color: 'medleyGreen',
        title: 'Leader updated',
        message: `Session ${session.session_number ?? ''} leader set.`,
      });
      onUpdated?.();
    } catch (err) {
      // MED-906 follow-up: the axios interceptor in services/api/client.ts
      // flattens API errors to Error & { status, code, message } — there is no
      // `err.response`. The previous `apiErr.response?.data?.code` pattern was
      // always undefined, so the friendly per-code messages never fired.
      const apiErr = err as Error & { status?: number; code?: string };
      const code = apiErr.code;
      const friendlyMessage =
        code === 'session_already_started'
          ? 'Session has already started; leader cannot be changed.'
          : code === 'participant_not_in_group'
            ? 'Selected user is no longer a member of this group.'
            : code === 'not_peer_led'
              ? 'This session is not configured for peer leadership.'
              : (apiErr.message ?? 'Failed to update session leader.');
      notifications.show({
        color: 'dangerRed',
        title: 'Could not set leader',
        message: friendlyMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const selectData = groupMembers.map((m) => ({
    value: m.user_id,
    label: formatMemberLabel(m),
  }));

  return (
    <Group
      gap={6}
      wrap="nowrap"
      onClick={(e) => e.stopPropagation()}
      style={{ minWidth: 200 }}
    >
      <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
        Leader
      </Text>
      <Select
        size="xs"
        searchable
        data={selectData}
        value={session.session_leader_id ?? null}
        placeholder="Unassigned"
        nothingFoundMessage="No members"
        disabled={isSaving}
        onChange={handleChange}
        comboboxProps={{ withinPortal: true }}
        style={{ minWidth: 160 }}
      />
      {isSaving && <Loader size="xs" />}
    </Group>
  );
}

export default SessionLeaderCell;
